import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../Components/PageHeader";
import AddPatientForm from "./AddPatientForm";
import SearchPatientInput from "./SearchPatientInput";
import SelectPatientTable from "./SelectPatientTable";
import SuccessMessage from "../../Components/shared/SuccessMessage";

function BookSlotPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const doctorScheduleId = searchParams.get("doctorScheduleId");
  const doctorName = searchParams.get("doctorName");
  const departmentName = searchParams.get("departmentName");
  const date = searchParams.get("date");
  const type = searchParams.get("type");
  const token = searchParams.get("token");
  const startTime = searchParams.get("startTime");
  const endTime = searchParams.get("endTime");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [patient, setPatient] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);
  const [showSelectPatientTable, setShowSelectPatientTable] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  const [newPatient, setNewPatient] = useState({
    name: "",
    age: "",
    gender: "male",
  });

  const [bookingStatus, setBookingStatus] = useState("");

  const handleCreatePatient = async () => {
    try {
      // Use phoneNumber from newPatient if available, otherwise use the phoneNumber state
      const finalPhoneNumber = newPatient.phoneNumber || phoneNumber;

      const patientData = {
        name: newPatient.name,
        phoneNumber: finalPhoneNumber,
        age: newPatient.age,
        gender: newPatient.gender,
        notes: newPatient.place || newPatient.notes || null,
      };

      console.log("Creating patient with:", patientData);

      const res = await axiosInstance.post("/patients", patientData);
      setPatient(res.data);
      setNotFound(false);
      setShowAddPatientForm(false);
      setShowSelectPatientTable(false);
    } catch (err) {
      console.error("Failed to create patient:", err);
      console.error("Error response:", err.response?.data);
      alert(err.response?.data?.message || "Failed to create patient.");
    }
  };

  const handleBookSlot = async () => {
    try {
      await axiosInstance.post("/bookings", {
        doctorScheduleId,
        bookingDate: date,
        tokenNumber: token ? parseInt(token) : null,
        startTime,
        endTime,
        patientId: patient.id,
        status: "booked",
      });
      setBookingStatus("success");
    } catch (err) {
      console.error("Booking failed:", err);
      setBookingStatus("error");
    }
  };

  const handleCloseSuccessMessage = () => {
    setBookingStatus("");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <PageHeader
        title="Book Slot"
        subtitle="Search for a patient and book an appointment"
        showBackButton={true}
      />

      <div className="px-4 py-6 max-w-4xl mx-auto">
        {/* Slot Details Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Slot Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">Doctor:</span>
                  <span className="font-medium text-gray-900">
                    {doctorName}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">Department:</span>
                  <span className="font-medium text-gray-900">
                    {departmentName}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4M8 7H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3M8 7v5a1 1 0 001 1h6a1 1 0 001-1V7"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="font-medium text-gray-900">{date}</span>
                </div>

                <div className="flex items-center gap-3">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">Time:</span>
                  <span className="font-medium text-gray-900">
                    {startTime} – {endTime}
                  </span>
                </div>

                {type !== "interval" && token && (
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                      />
                    </svg>
                    <span className="text-sm text-gray-600">Token #:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {token}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Patient Search Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Find Patient
                </h3>
              </div>

              <button
                onClick={() => {
                  setShowAddPatientForm(true);
                  setPatient(null);
                  setShowSelectPatientTable(false);
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Patient
              </button>
            </div>

            <SearchPatientInput
              onFound={(foundPatient) => {
                setSearchResult(foundPatient);
                setShowAddPatientForm(false);
                setShowSelectPatientTable(true);
                setPhoneNumber(foundPatient.phone);
                setBookingStatus("");
              }}
              onNotFound={(searchedInput) => {
                const isPhone = /^\d{10}$/.test(searchedInput);
                setPatient(null);
                setPhoneNumber(searchedInput);
                setNotFound(true);
                setSearchResult(null);

                setShowSelectPatientTable(true);
                setShowAddPatientForm(isPhone);

                setBookingStatus("");
              }}
            />
          </div>
        </div>

        {/* Selected Patient Card */}
        {patient && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Selected Patient
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                  <span className="text-sm text-gray-600">Name</span>
                  <p className="font-medium text-gray-900">{patient.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Age</span>
                  <p className="font-medium text-gray-900">
                    {patient.age} years
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Gender</span>
                  <p className="font-medium text-gray-900 capitalize">
                    {patient.gender}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Phone</span>
                  <p className="font-medium text-gray-900">
                    {patient.phoneNumber}
                  </p>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleBookSlot}
                  className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-md font-medium flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Confirm Booking
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {bookingStatus === "success" && (
          <SuccessMessage
            message="Booking confirmed! Redirecting to dashboard..."
            isError={false}
            onClose={handleCloseSuccessMessage}
            duration={2000}
          />
        )}

        {bookingStatus === "error" && (
          <SuccessMessage
            message="Booking failed! Unable to book the appointment slot. Please try again."
            isError={true}
            onClose={() => setBookingStatus("")}
            duration={3000}
          />
        )}

        {/* Modal Components */}
        {showAddPatientForm && (
          <AddPatientForm
            phoneNumber={phoneNumber}
            newPatient={newPatient}
            setNewPatient={setNewPatient}
            onSubmit={handleCreatePatient}
            onClose={() => setShowAddPatientForm(false)}
          />
        )}

        {showSelectPatientTable && (
          <SelectPatientTable
            searchResult={searchResult}
            onSelectPatient={(selected) => {
              setPatient(selected);
              setPhoneNumber(selected.phone);
              setShowSelectPatientTable(false);
              setShowAddPatientForm(false);
              setBookingStatus("");
            }}
            onClose={() => setShowSelectPatientTable(false)}
          />
        )}
      </div>
    </div>
  );
}

export default BookSlotPage;
